import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
  TablePagination,
} from '@mui/material';
import { Edit, Delete, GetApp, Visibility, Warning, ArrowBack, Close as CloseIcon, ContentCopy } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import businessPolicyService from '../services/businessPolicyService';
import RichTextEditor from '../components/RichTextEditor';
import { useActionLock } from '../hooks/useActionLock';
import ActionButton from '../components/ActionButton';

const Policies = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [viewingPolicy, setViewingPolicy] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [policyToPublish, setPolicyToPublish] = useState(null);
  const [saveAndPublishPending, setSaveAndPublishPending] = useState(false);
  const [formData, setFormData] = useState({
    policy_type: '',
    version: '',
    content_preview: '',
  });

  // API state (action locks prevent double submit)
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // 'publish' when save+publish in progress
  const [draftLocked, runSaveDraft] = useActionLock();
  const [deleting, runDeleteConfirm] = useActionLock();
  const [publishing, runPublishConfirm] = useActionLock();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const hasFetched = useRef(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  console.log("formDataformData", formData)

  // Helper function to format date as dd-mm-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper to get display name for policy type
  const getPolicyTypeDisplay = (type) => {
    const typeMap = {
      privacy_policy: 'Privacy Policy',
      terms_of_service: 'Terms of Service',
      asset_sharing_consent: 'Asset Sharing Consent',
      auto_debit_consent: 'Auto Debit Consent',
      cancellation_refund_policy: 'Cancellation & Refund Policy',
      cookies_policy: 'Cookies Policy',
    };
    return typeMap[type] || type;
  };

  // Fetch policies function with pagination
  const fetchPolicies = async (currentPage = page, currentPageSize = pageSize) => {
    setLoading(true);
    setError('');

    const result = await businessPolicyService.getPolicies({
      page: currentPage + 1, // API uses 1-based pagination
      page_size: currentPageSize,
    });

    if (result.success) {
      setPolicies(result.data.results || result.data || []);
      setTotalCount(result.data.count || 0);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Fetch policies on component mount
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchPolicies();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const refreshPolicies = async () => {
    const result = await businessPolicyService.getPolicies({
      page: page + 1,
      page_size: pageSize,
    });
    if (result.success) {
      setPolicies(result.data.results || result.data || []);
      setTotalCount(result.data.count || 0);
    }
  };

  const handleAddNew = () => {
    setFormData({
      policy_type: '',
      version: '',
      content_preview: '',
    });
    setEditingPolicy(null);
    setShowAddForm(true);
  };

  const handleEdit = async (policy) => {
    // Fetch full policy details to get complete content
    setLoading(true);

    try {
      const result = await businessPolicyService.getPolicyById(policy.policy_id);

      if (result.success) {
        const fullPolicy = result.data;
        setFormData({
          policy_type: fullPolicy.policy_type,
          version: fullPolicy.version,
          content_preview: fullPolicy.content_preview || '',
          content: fullPolicy.content || '',
        });
        setEditingPolicy(fullPolicy);
        setShowAddForm(true);
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('Failed to load policy details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (policy) => {
    // Fetch full policy details to get complete content
    setLoading(true);

    try {
      const result = await businessPolicyService.getPolicyById(policy.policy_id);

      if (result.success) {
        setViewingPolicy(result.data);
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('Failed to load policy details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (policy) => {
    setPolicyToDelete(policy);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await businessPolicyService.deletePolicy(policyToDelete.policy_id);

      if (result.success) {
        showSnackbar('Policy deleted successfully');
        await refreshPolicies();
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while deleting', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setPolicyToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPolicyToDelete(null);
  };

  // Helper to get the currently active policy for a given type
  const getCurrentlyActivePolicy = (policyType) => {
    return policies.find(p => p.policy_type === policyType && p.status === 'active');
  };

  const handlePublishClick = (policy) => {
    if (policy.status === 'published' || policy.status === 'active') {
      showSnackbar('This policy is already published', 'info');
      return;
    }

    setPolicyToPublish(policy);
    setPublishDialogOpen(true);
  };

  const handlePublishConfirm = async () => {
    if (saveAndPublishPending) {
      setPublishDialogOpen(false);
      setPolicyToPublish(null);
      setSaveAndPublishPending(false);
      await handleSaveAndPublish();
      return;
    }

    if (!policyToPublish) return;

    try {
      const result = await businessPolicyService.publishPolicy(policyToPublish.policy_id);

      if (result.success) {
        showSnackbar('Policy published successfully');
        await refreshPolicies();
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while publishing', 'error');
    } finally {
      setPublishDialogOpen(false);
      setPolicyToPublish(null);
    }
  };

  const handlePublishCancel = () => {
    setPublishDialogOpen(false);
    setPolicyToPublish(null);
    setSaveAndPublishPending(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await businessPolicyService.exportToExcel();

      if (result.success) {
        // Create a blob URL and trigger download
        const blob = new Blob([result.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `business_policies_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showSnackbar('Excel file downloaded successfully');
      } else {
        showSnackbar(result.error || 'Failed to export Excel', 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while exporting', 'error');
    } finally {
      setExporting(false);
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchPolicies(newPage, pageSize);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPage(0);
    fetchPolicies(0, newPageSize);
  };

  const handleSaveDraft = async () => {
    // Validate policy type
    if (!formData?.policy_type?.trim()) {
      showSnackbar('Please select a policy type', 'error');
      return;
    }

    // Validate version
    if (!formData?.version?.trim()) {
      showSnackbar('Please enter a version', 'error');
      return;
    }

    // Validate version format (X.Y.Z)
    const versionRegex = /^\d+\.\d+\.\d+$/;
    if (!versionRegex.test(formData.version.trim())) {
      showSnackbar('Version must be in format X.Y.Z (e.g., 1.0.0)', 'error');
      return;
    }

    // Validate content
    if (!formData?.content_preview?.trim() || formData.content_preview.trim() === '<p></p>' || formData.content_preview.trim() === '<p><br></p>') {
      showSnackbar('Please enter policy content', 'error');
      return;
    }

    // Check for duplicate policy version
    const isDuplicate = policies.some(p =>
      p.policy_type === formData.policy_type &&
      p.version === formData.version &&
      (!editingPolicy || p.policy_id !== editingPolicy.policy_id)
    );

    if (isDuplicate) {
      showSnackbar(`A policy with type '${getPolicyTypeDisplay(formData.policy_type)}' and version '${formData.version}' already exists.`, 'error');
      return;
    }

    try {
      let result;

      if (editingPolicy) {
        result = await businessPolicyService.updatePolicy(editingPolicy.policy_id, {
          policy_type: formData.policy_type,
          version: formData.version,
          content: formData.content_preview,
        });
      } else {
        result = await businessPolicyService.createPolicy({
          policy_type: formData.policy_type,
          version: formData.version,
          content: formData.content_preview,
        });
      }

      if (result.success) {
        showSnackbar(editingPolicy ? 'Policy updated successfully' : 'Policy saved as draft');
        await refreshPolicies();
        setShowAddForm(false);
        setEditingPolicy(null);
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while saving', 'error');
    }
  };

  const handleSaveAndPublishClick = () => {
    // Validate policy type
    if (!formData?.policy_type?.trim()) {
      showSnackbar('Please select a policy type', 'error');
      return;
    }

    // Validate version
    if (!formData?.version?.trim()) {
      showSnackbar('Please enter a version', 'error');
      return;
    }

    // Validate version format (X.Y.Z)
    const versionRegex = /^\d+\.\d+\.\d+$/;
    if (!versionRegex.test(formData.version.trim())) {
      showSnackbar('Version must be in format X.Y.Z (e.g., 1.0.0)', 'error');
      return;
    }

    // Validate content
    if (!formData?.content_preview?.trim() || formData.content_preview.trim() === '<p></p>' || formData.content_preview.trim() === '<p><br></p>') {
      showSnackbar('Please enter policy content', 'error');
      return;
    }

    setSaveAndPublishPending(true);
    setPublishDialogOpen(true);
  };

  const handleSaveAndPublish = async () => {
    // Validate policy type
    if (!formData?.policy_type?.trim()) {
      showSnackbar('Please select a policy type', 'error');
      return;
    }

    // Validate version
    if (!formData?.version?.trim()) {
      showSnackbar('Please enter a version', 'error');
      return;
    }

    // Validate version format (X.Y.Z)
    const versionRegex = /^\d+\.\d+\.\d+$/;
    if (!versionRegex.test(formData.version.trim())) {
      showSnackbar('Version must be in format X.Y.Z (e.g., 1.0.0)', 'error');
      return;
    }

    // Validate content
    if (!formData?.content_preview?.trim() || formData.content_preview.trim() === '<p></p>' || formData.content_preview.trim() === '<p><br></p>') {
      showSnackbar('Please enter policy content', 'error');
      return;
    }

    // Check for duplicate policy version
    const isDuplicate = policies.some(p =>
      p.policy_type === formData.policy_type &&
      p.version === formData.version &&
      (!editingPolicy || p.policy_id !== editingPolicy.policy_id)
    );

    if (isDuplicate) {
      showSnackbar(`A policy with type '${getPolicyTypeDisplay(formData.policy_type)}' and version '${formData.version}' already exists.`, 'error');
      return;
    }

    setActionLoading('publish');

    try {
      let result;

      if (editingPolicy) {
        // Update first, then publish
        result = await businessPolicyService.updatePolicy(editingPolicy.policy_id, {
          policy_type: formData.policy_type,
          version: formData.version,
          content: formData.content_preview,
        });

        if (result.success && result.data.status !== 'published') {
          result = await businessPolicyService.publishPolicy(editingPolicy.policy_id);
        }
      } else {
        // Create and publish in one call
        result = await businessPolicyService.createAndPublishPolicy({
          policy_type: formData.policy_type,
          version: formData.version,
          content: formData.content_preview,
        });
      }

      if (result.success) {
        showSnackbar(editingPolicy ? 'Policy updated and published' : 'Policy created and published successfully');
        await refreshPolicies();
        setShowAddForm(false);
        setEditingPolicy(null);
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while publishing', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // View Policy Page
  if (viewingPolicy) {
    return (
      <Box sx={{
        backgroundColor: '#F8FAFC',
        minHeight: '100vh',
        padding: { xs: 2, sm: 2.5, md: 3 }
      }}>
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 1.5, sm: 2 },
            marginBottom: { xs: 2, md: 3 },
            borderRadius: 2,
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            color: 'white',
            border: '1px solid #ECF0F1',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => setViewingPolicy(null)}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '20px' }}>
              View Policy
            </Typography>
          </Box>
        </Paper>

        {/* Policy Details */}
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: '1px solid #ECF0F1',
            backgroundColor: '#FFFFFF',
          }}
        >
          {/* Policy Information Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 2, sm: 3 }, marginBottom: { xs: 2, sm: 3 } }}>
            {/* Policy Type */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 1,
                }}
              >
                Policy Type
              </Typography>
              <Typography
                sx={{
                  color: '#1E293B',
                  fontSize: '16px',
                  fontWeight: 600,
                }}
              >
                {getPolicyTypeDisplay(viewingPolicy.policy_type)}
              </Typography>
            </Box>

            {/* Version */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 1,
                }}
              >
                Version
              </Typography>
              <Typography
                sx={{
                  color: '#1E293B',
                  fontSize: '16px',
                  fontWeight: 600,
                }}
              >
                {viewingPolicy.version}
              </Typography>
            </Box>

            {/* Status */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 1,
                }}
              >
                Status
              </Typography>
              <Chip
                label={
                  viewingPolicy.status === 'active'
                    ? 'Active'
                    : viewingPolicy.status === 'published'
                      ? 'Published'
                      : viewingPolicy.status === 'previously_published'
                        ? 'Previously Published'
                        : 'Draft'
                }
                sx={{
                  backgroundColor:
                    viewingPolicy.status === 'active'
                      ? '#DBEAFE'
                      : viewingPolicy.status === 'published'
                        ? '#D4F4DD'
                        : viewingPolicy.status === 'previously_published'
                          ? '#E0E7FF'
                          : '#FFF4E6',
                  color:
                    viewingPolicy.status === 'active'
                      ? '#1D4ED8'
                      : viewingPolicy.status === 'published'
                        ? '#2E7D32'
                        : viewingPolicy.status === 'previously_published'
                          ? '#4338CA'
                          : '#B17C42',
                  fontWeight: 600,
                  fontSize: '13px',
                  height: '28px',
                  textTransform: 'capitalize',
                }}
              />
            </Box>

            {/* Published On */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 1,
                }}
              >
                Published On
              </Typography>
              <Typography
                sx={{
                  color: '#1E293B',
                  fontSize: '16px',
                  fontWeight: 600,
                }}
              >
                {(viewingPolicy.status === 'published' || viewingPolicy.status === 'active' || viewingPolicy.status === 'previously_published') && viewingPolicy.published_at
                  ? formatDate(viewingPolicy.published_at)
                  : 'Not Published'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ marginY: 3 }} />

          {/* Policy Content */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Policy Content
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  // Get plain text from HTML content
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = viewingPolicy.content || viewingPolicy.content_preview || '';
                  const plainText = tempDiv.textContent || tempDiv.innerText || '';

                  navigator.clipboard.writeText(plainText).then(() => {
                    showSnackbar('Policy content copied to clipboard', 'success');
                  }).catch(() => {
                    showSnackbar('Failed to copy content', 'error');
                  });
                }}
                sx={{
                  color: '#64748B',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: '#4E83BD',
                    color: 'white',
                    borderColor: '#4E83BD',
                  },
                }}
                title="Copy content"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Box>
            <Paper
              elevation={0}
              sx={{
                padding: 3,
                backgroundColor: '#F8FAFC',
                border: '1px solid #ECF0F1',
                borderRadius: 2,
                minHeight: '200px',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  color: '#2C3E50',
                  fontSize: '14px',
                  lineHeight: 1.8,
                  '& p': { margin: 0, marginBottom: '0.5em' },
                  '& p:last-child': { marginBottom: 0 },
                  '& strong': { fontWeight: 600 },
                  '& em': { fontStyle: 'italic' },
                  '& u': { textDecoration: 'underline' },
                  '& s': { textDecoration: 'line-through' },
                }}
                dangerouslySetInnerHTML={{ __html: viewingPolicy.content || viewingPolicy.content_preview || 'No content available' }}
              />
            </Paper>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 3, gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={() => setViewingPolicy(null)}
              sx={{
                borderColor: '#ECF0F1',
                color: '#2C3E50',
                textTransform: 'none',
                paddingX: 2.5,
                fontSize: '14px',
                '&:hover': {
                  borderColor: '#4E83BD',
                  backgroundColor: '#F8FAFC',
                },
              }}
            >
              Close
            </Button>
            {viewingPolicy.status !== 'published' && viewingPolicy.status !== 'active' && viewingPolicy.status !== 'previously_published' && (
              <Button
                variant="contained"
                onClick={() => {
                  setViewingPolicy(null);
                  handleEdit(viewingPolicy);
                }}
                sx={{
                  backgroundColor: '#4E83BD',
                  textTransform: 'none',
                  paddingX: 3,
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: '#3A6A99',
                  },
                }}
              >
                Edit Policy
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }

  // Add/Edit Policy Dialog is now at the bottom of the component

  return (
    <Box sx={{
      backgroundColor: '#F8FAFC',
      minHeight: '100vh',
      padding: { xs: 2, sm: 2.5, md: 3 }
    }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header - matching Dashboard style */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2 },
          marginBottom: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
          color: 'white',
          border: '1px solid #ECF0F1',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 0 },
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' } }}>
            Business Policy
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
            <Button
              variant="contained"
              onClick={handleAddNew}
              sx={{
                textTransform: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: { xs: '11px', sm: '13px' },
                padding: { xs: '6px 12px', sm: '8px 16px' },
                borderRadius: '6px',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              Add New Policy
            </Button>

            <Button
              variant="contained"
              onClick={handleExport}
              disabled={exporting}
              sx={{
                textTransform: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: { xs: '11px', sm: '13px' },
                padding: { xs: '6px 12px', sm: '8px 16px' },
                borderRadius: '6px',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.7)',
                },
              }}
            >
              {exporting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} sx={{ color: 'white' }} />
                  <span>Exporting...</span>
                </Box>
              ) : (
                'Export as Excel'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} sx={{ color: '#4E83BD' }} />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ marginBottom: 3 }}>
          {error}
        </Alert>
      )}

      {/* Table - Desktop View */}
      {!loading && !error && !isMobile && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #ECF0F1',
          }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: '#4E83BD',
                  }}
                >
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      borderBottom: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    Policy Type
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      borderBottom: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    Version
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      borderBottom: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      borderBottom: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    Published On
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      borderBottom: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {policies.map((policy, index) => (
                  <TableRow
                    key={policy.policy_id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F8FAFC',
                      '&:hover': {
                        backgroundColor: '#E8F4F8',
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        borderBottom: '1px solid #ECF0F1',
                        padding: '12px 16px',
                      }}
                    >
                      {getPolicyTypeDisplay(policy.policy_type)}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        borderBottom: '1px solid #ECF0F1',
                        padding: '12px 16px',
                      }}
                    >
                      {policy.version}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #ECF0F1',
                        padding: '12px 16px',
                      }}
                    >
                      <Chip
                        label={
                          policy.status === 'active'
                            ? 'Active'
                            : policy.status === 'published'
                              ? 'Published'
                              : policy.status === 'previously_published'
                                ? 'Previously Published'
                                : 'Draft'
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            policy.status === 'active'
                              ? '#DBEAFE'
                              : policy.status === 'published'
                                ? '#D4F4DD'
                                : policy.status === 'previously_published'
                                  ? '#E0E7FF'
                                  : '#FFF4E6',
                          color:
                            policy.status === 'active'
                              ? '#1D4ED8'
                              : policy.status === 'published'
                                ? '#2E7D32'
                                : policy.status === 'previously_published'
                                  ? '#4338CA'
                                  : '#B17C42',
                          fontWeight: 600,
                          fontSize: '11px',
                          height: '22px',
                          textTransform: 'capitalize',
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        borderBottom: '1px solid #ECF0F1',
                        padding: '12px 16px',
                      }}
                    >
                      {(policy.status === 'published' || policy.status === 'active' || policy.status === 'previously_published') && policy.published_at
                        ? formatDate(policy.published_at)
                        : '-'}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #ECF0F1',
                        padding: '12px 16px',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(policy)}
                          disabled={policy.status === 'published' || policy.status === 'active' || policy.status === 'previously_published'}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                            '&.Mui-disabled': {
                              color: '#94a3b8',
                              backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            },
                          }}
                          title={policy.status === 'published' || policy.status === 'active' || policy.status === 'previously_published' ? 'Cannot edit published policies' : 'Edit Policy'}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(policy)}
                          disabled={policy.status === 'published' || policy.status === 'active' || policy.status === 'previously_published'}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                            '&.Mui-disabled': {
                              color: '#94a3b8',
                              backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            },
                          }}
                          title={policy.status === 'published' || policy.status === 'active' || policy.status === 'previously_published' ? 'Cannot delete published policies' : 'Delete Policy'}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handlePublishClick(policy)}
                          disabled={publishing === policy.policy_id || policy.status === 'published' || policy.status === 'active'}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                            '&.Mui-disabled': {
                              color: '#94a3b8',
                              backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            },
                          }}
                          title={(policy.status === 'published' || policy.status === 'active') ? 'Already Published' : 'Publish Policy'}
                        >
                          {publishing === policy.policy_id ? (
                            <CircularProgress size={16} sx={{ color: '#4E83BD' }} />
                          ) : (
                            <GetApp fontSize="small" />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleView(policy)}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Empty State */}
          {policies.length === 0 && !loading && (
            <Box sx={{ padding: 6, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#64748b', marginBottom: 1 }}>
                No Policies Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', marginBottom: 3 }}>
                Get started by creating your first business policy
              </Typography>
              <Button
                variant="contained"
                onClick={handleAddNew}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#4E83BD',
                  '&:hover': {
                    backgroundColor: '#3A6A99',
                  },
                }}
              >
                Add New Policy
              </Button>
            </Box>
          )}

          {/* Pagination */}
          {policies.length > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: '1px solid #ECF0F1',
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  color: '#64748B',
                  fontSize: '13px',
                },
                '.MuiTablePagination-select': {
                  color: '#2C3E50',
                },
                '.MuiTablePagination-actions': {
                  color: '#2C3E50',
                },
              }}
            />
          )}
        </Paper>
      )}

      {/* Mobile Card View */}
      {!loading && !error && isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {policies.map((policy) => (
            <Paper
              key={policy.policy_id}
              elevation={0}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #ECF0F1',
                backgroundColor: '#FFFFFF',
              }}
            >
              {/* Card Header */}
              <Box
                sx={{
                  backgroundColor: '#4E83BD',
                  padding: '10px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Chip
                  label={
                    policy.status === 'active'
                      ? 'Active'
                      : policy.status === 'published'
                        ? 'Published'
                        : policy.status === 'previously_published'
                          ? 'Prev. Published'
                          : 'Draft'
                  }
                  size="small"
                  sx={{
                    backgroundColor:
                      policy.status === 'active'
                        ? '#DBEAFE'
                        : policy.status === 'published'
                          ? '#D4F4DD'
                          : policy.status === 'previously_published'
                            ? '#E0E7FF'
                            : '#FFF4E6',
                    color:
                      policy.status === 'active'
                        ? '#1D4ED8'
                        : policy.status === 'published'
                          ? '#2E7D32'
                          : policy.status === 'previously_published'
                            ? '#4338CA'
                            : '#B17C42',
                    fontWeight: 600,
                    fontSize: '11px',
                    height: '22px',
                    textTransform: 'capitalize',
                  }}
                />
                <Typography sx={{ color: 'white', fontSize: '12px' }}>
                  {(policy.status === 'published' || policy.status === 'active' || policy.status === 'previously_published') && policy.published_at
                    ? formatDate(policy.published_at)
                    : '-'}
                </Typography>
              </Box>

              {/* Card Content */}
              <Box sx={{ padding: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 0.5,
                  }}
                >
                  Policy Type
                </Typography>
                <Typography
                  sx={{
                    color: '#2C3E50',
                    fontSize: '14px',
                    fontWeight: 600,
                    marginBottom: 1.5,
                  }}
                >
                  {getPolicyTypeDisplay(policy.policy_type)}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 0.5,
                  }}
                >
                  Version
                </Typography>
                <Typography
                  sx={{
                    color: '#2C3E50',
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  {policy.version}
                </Typography>
              </Box>

              {/* Card Actions */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 0.5,
                  padding: '8px 16px',
                  borderTop: '1px solid #ECF0F1',
                  backgroundColor: '#F8FAFC',
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleEdit(policy)}
                  disabled={policy.status === 'published' || policy.status === 'active' || policy.status === 'previously_published'}
                  sx={{
                    color: '#4E83BD',
                    backgroundColor: 'rgba(78, 131, 189, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(78, 131, 189, 0.2)',
                    },
                    '&.Mui-disabled': {
                      color: '#94a3b8',
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    },
                  }}
                  title={policy.status === 'published' || policy.status === 'active' || policy.status === 'previously_published' ? 'Cannot edit published policies' : 'Edit Policy'}
                >
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(policy)}
                  disabled={policy.status === 'published' || policy.status === 'active' || policy.status === 'previously_published'}
                  sx={{
                    color: '#4E83BD',
                    backgroundColor: 'rgba(78, 131, 189, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(78, 131, 189, 0.2)',
                    },
                    '&.Mui-disabled': {
                      color: '#94a3b8',
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    },
                  }}
                  title={policy.status === 'published' || policy.status === 'active' || policy.status === 'previously_published' ? 'Cannot delete published policies' : 'Delete Policy'}
                >
                  <Delete fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handlePublishClick(policy)}
                  disabled={publishing === policy.policy_id || policy.status === 'published' || policy.status === 'active'}
                  sx={{
                    color: '#4E83BD',
                    backgroundColor: 'rgba(78, 131, 189, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(78, 131, 189, 0.2)',
                    },
                    '&.Mui-disabled': {
                      color: '#94a3b8',
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    },
                  }}
                  title={(policy.status === 'published' || policy.status === 'active') ? 'Already Published' : 'Publish Policy'}
                >
                  {publishing === policy.policy_id ? (
                    <CircularProgress size={16} sx={{ color: '#4E83BD' }} />
                  ) : (
                    <GetApp fontSize="small" />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleView(policy)}
                  sx={{
                    color: '#4E83BD',
                    backgroundColor: 'rgba(78, 131, 189, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(78, 131, 189, 0.2)',
                    },
                  }}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))}

          {/* Empty State for Mobile */}
          {policies.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid #ECF0F1',
                padding: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: '#64748b', marginBottom: 1 }}>
                No Policies Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', marginBottom: 3 }}>
                Get started by creating your first business policy
              </Typography>
              <Button
                variant="contained"
                onClick={handleAddNew}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#4E83BD',
                  '&:hover': {
                    backgroundColor: '#3A6A99',
                  },
                }}
              >
                Add New Policy
              </Button>
            </Paper>
          )}

          {/* Pagination for Mobile */}
          {policies.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid #ECF0F1',
                overflow: 'hidden',
              }}
            >
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  '.MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    padding: '8px',
                  },
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    color: '#64748B',
                    fontSize: '12px',
                  },
                  '.MuiTablePagination-select': {
                    color: '#2C3E50',
                  },
                  '.MuiTablePagination-actions': {
                    color: '#2C3E50',
                  },
                }}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 1,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                backgroundColor: '#FEE2E2',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Warning sx={{ color: '#DC2626', fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
              Delete Policy
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#2C3E50', fontSize: '14px', lineHeight: 1.6 }}>
            Are you sure you want to delete the policy{' '}
            <strong>"{getPolicyTypeDisplay(policyToDelete?.policy_type)}"</strong> (Version: {policyToDelete?.version})?
            <br />
            <br />
            This action cannot be undone and the policy will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            disabled={deleting}
            sx={{
              borderColor: '#ECF0F1',
              color: '#2C3E50',
              textTransform: 'none',
              paddingX: 2.5,
              fontSize: '14px',
              '&:hover': {
                borderColor: '#4E83BD',
                backgroundColor: '#F8FAFC',
              },
            }}
          >
            Cancel
          </Button>
          <ActionButton
            onClick={() => runDeleteConfirm(handleDeleteConfirm)}
            variant="contained"
            loading={deleting}
            loadingText="Deleting..."
            sx={{
              backgroundColor: '#DC2626',
              textTransform: 'none',
              paddingX: 3,
              fontSize: '14px',
              '&:hover': {
                backgroundColor: '#B91C1C',
              },
            }}
          >
            Delete
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog
        open={publishDialogOpen}
        onClose={handlePublishCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 1,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                backgroundColor: '#DBEAFE',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Warning sx={{ color: '#4E83BD', fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
              Confirm Publish
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#2C3E50', fontSize: '14px', lineHeight: 1.6 }}>
            Once this content is published, it cannot be edited or deleted. Please review the information carefully before continuing.
            {policyToPublish && !saveAndPublishPending && (
              <>
                <br /><br />
                You are about to publish Policy Type{' '}
                <strong>'{getPolicyTypeDisplay(policyToPublish?.policy_type)}'</strong> Version{' '}
                <strong>'{policyToPublish?.version}'</strong>.
                {getCurrentlyActivePolicy(policyToPublish?.policy_type) && (
                  <>
                    {' '}This will replace the currently published version{' '}
                    <strong>'{getCurrentlyActivePolicy(policyToPublish?.policy_type)?.version}'</strong>.
                  </>
                )}
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handlePublishCancel}
            variant="outlined"
            disabled={publishing}
            sx={{
              borderColor: '#ECF0F1',
              color: '#2C3E50',
              textTransform: 'none',
              paddingX: 2.5,
              fontSize: '14px',
              '&:hover': {
                borderColor: '#4E83BD',
                backgroundColor: '#F8FAFC',
              },
            }}
          >
            Cancel
          </Button>
          <ActionButton
            onClick={() => runPublishConfirm(handlePublishConfirm)}
            variant="contained"
            loading={publishing}
            loadingText="Publishing..."
            sx={{
              backgroundColor: '#4E83BD',
              textTransform: 'none',
              paddingX: 3,
              fontSize: '14px',
              '&:hover': {
                backgroundColor: '#3A6A99',
              },
            }}
          >
            Yes, Publish
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Policy Dialog */}
      <Dialog
        open={showAddForm}
        onClose={() => {
          setShowAddForm(false);
          setEditingPolicy(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            maxHeight: '90vh',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', fontSize: '18px' }}>
            {editingPolicy ? 'Edit Policy' : 'Add New Policy'}
          </Typography>
          <IconButton
            onClick={() => {
              setShowAddForm(false);
              setEditingPolicy(null);
            }}
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ padding: 0, overflow: 'auto', maxHeight: 'calc(90vh - 76px)' }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: '100%' }}>
            {/* Left Section - Policy Details */}
            <Box sx={{
              flex: '0 0 40%',
              padding: 3,
              borderRight: { md: '1px solid #E5E7EB' },
              borderBottom: { xs: '1px solid #E5E7EB', md: 'none' },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2.5 }}>
                <Box sx={{ width: 4, height: 20, bgcolor: '#4E83BD', borderRadius: 1 }} />
                <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                  Policy Details
                </Typography>
              </Box>

              {/* Policy Type */}
              <Box sx={{ marginBottom: 2.5 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Policy Type <span style={{ color: '#DC2626' }}>*</span>
                </Typography>
                <Select
                  fullWidth
                  displayEmpty
                  size="small"
                  value={formData.policy_type}
                  onChange={(e) => setFormData({ ...formData, policy_type: e.target.value })}
                  IconComponent={KeyboardArrowDownIcon}
                  sx={{
                    borderRadius: '10px',
                    backgroundColor: '#F8FAFC',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4E83BD' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4E83BD' },
                    '& .MuiSelect-select': { fontSize: '14px', color: '#1E293B' },
                  }}
                >
                  <MenuItem value="" disabled>Select policy type</MenuItem>
                  <MenuItem value="privacy_policy">Privacy Policy</MenuItem>
                  <MenuItem value="terms_of_service">Terms of Service</MenuItem>
                  <MenuItem value="asset_sharing_consent">Asset Sharing Consent</MenuItem>
                  <MenuItem value="auto_debit_consent">Auto Debit Consent</MenuItem>
                  <MenuItem value="cancellation_refund_policy">Cancellation & Refund Policy</MenuItem>
                  <MenuItem value="cookies_policy">Cookies Policy</MenuItem>
                </Select>
              </Box>

              {/* Version */}
              <Box sx={{ marginBottom: 2.5 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Version <span style={{ color: '#DC2626' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g., 1.0.0"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: '#F8FAFC',
                      '& fieldset': { borderColor: '#E5E7EB' },
                      '&:hover fieldset': { borderColor: '#4E83BD' },
                      '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
                    },
                    '& .MuiInputBase-input': { fontSize: '14px', color: '#1E293B' },
                  }}
                />
              </Box>

              {/* Action Buttons - desktop only (md+): shown in left column */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 1.5, marginTop: 4, width: '100%' }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSaveAndPublishClick}
                  disabled={draftLocked || actionLoading !== null}
                  sx={{
                    textTransform: 'none',
                    background: '#4a7eb5',
                    fontWeight: 600,
                    padding: '14px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    boxShadow: '0 4px 12px rgba(78, 131, 189, 0.4)',
                  }}
                >
                  {actionLoading === 'publish' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      <span>Publishing...</span>
                    </Box>
                  ) : (
                    'Save & Publish'
                  )}
                </Button>
                <ActionButton
                  variant="outlined"
                  fullWidth
                  loading={draftLocked}
                  onClick={() => runSaveDraft(handleSaveDraft)}
                  loadingText="Saving..."
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    color: '#4E83BD',
                    borderColor: '#4E83BD',
                    padding: '12px',
                    borderRadius: '10px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#3A6A99',
                      backgroundColor: '#F0F7FF',
                    },
                  }}
                >
                  Save as Draft
                </ActionButton>
                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingPolicy(null);
                  }}
                  disabled={draftLocked || actionLoading !== null}
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    color: '#64748B',
                    padding: '10px',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#F8FAFC',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>

            {/* Right Section - Policy Content */}
            <Box sx={{
              flex: 1,
              padding: 3,
              backgroundColor: '#FFFFFF',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2.5 }}>
                <Box sx={{ width: 4, height: 20, bgcolor: '#10B981', borderRadius: 1 }} />
                <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                  Policy Content
                </Typography>
              </Box>

              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Content <span style={{ color: '#DC2626' }}>*</span>
              </Typography>
              <Box sx={{ flex: 1, minHeight: '350px' }}>
                <RichTextEditor
                  value={formData.content}
                  onChange={(value) => setFormData({ ...formData, content_preview: value })}
                  placeholder="Type policy content here. Use the toolbar to format your text with bold, italic, underline, lists, and more..."
                  minHeight="350px"
                />
              </Box>

              {/* Action Buttons - responsive only: below Policy Content on xs/sm, full width */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, marginTop: 3, paddingTop: 2, borderTop: '1px solid #E5E7EB', width: '100%' }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSaveAndPublishClick}
                  disabled={draftLocked || actionLoading !== null}
                  sx={{
                    textTransform: 'none',
                    background: '#4a7eb5',
                    fontWeight: 600,
                    padding: '14px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    boxShadow: '0 4px 12px rgba(78, 131, 189, 0.4)',
                  }}
                >
                  {actionLoading === 'publish' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      <span>Publishing...</span>
                    </Box>
                  ) : (
                    'Save & Publish'
                  )}
                </Button>
                <ActionButton
                  variant="outlined"
                  fullWidth
                  loading={draftLocked}
                  onClick={() => runSaveDraft(handleSaveDraft)}
                  loadingText="Saving..."
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    color: '#4E83BD',
                    borderColor: '#4E83BD',
                    padding: '12px',
                    borderRadius: '10px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#3A6A99',
                      backgroundColor: '#F0F7FF',
                    },
                  }}
                >
                  Save as Draft
                </ActionButton>
                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingPolicy(null);
                  }}
                  disabled={draftLocked || actionLoading !== null}
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    color: '#64748B',
                    padding: '10px',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#F8FAFC',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Policies;
